import React, { Component } from "react";
import { matchPath } from "react-router-dom";
import BaseModal from "./base_modal";
import get from "lodash/get";
import { EpisodeEditForm, EpisodeCreateForm } from "../pages/episodes/episode_form";
import { getCroppedImg } from "../../lib/image-uploader";
import messageTypes from "../../constants/message_types";

class EpisodeFormModal extends Component {
  componentDidMount() {
    var showUUID = this.getShowUUID();
    if (showUUID) {
      this.props.fetchShow(showUUID, this.props.user);
    }

    var episodeUUID = this.getEpisodeUUID();
    if (showUUID && episodeUUID) {
      this.props.fetchEpisode(showUUID, episodeUUID, this.props.user).then(() => {
        var episode = this.getEpisode();
        if (episode) {
          this.props.fetchMediaFile(episode.contentMediaFileUUID, this.props.user.authToken);
          if (episode.imageMediaFileUUID) {
            this.props.fetchMediaFile(episode.imageMediaFileUUID, this.props.user.authToken);
          }
        }
      });
    }
  }

  isLoading = () => {
    var episodeInfo = get(this.props.episodesByShow, [this.getShowUUID()]);
    return this.props.isLoading || get(episodeInfo, "isPutting");
  };

  close = newEpisode => {
    this.props.history.push(this.props.closeRoute(this.props.location));
    this.props.closeModal();
  };

  getShowUUID = () => {
    var result = matchPath(this.props.location.pathname, "/shows/:showUUID/ep/:episodeUUID");
    return get(result, "params.showUUID");
  };

  getEpisodeUUID = () => {
    var result = matchPath(this.props.location.pathname, "/shows/:showUUID/ep/:episodeUUID");
    var episodeUUID = get(result, "params.episodeUUID");
    // on the create page, the path is such that the episode UUID will appear to be create.

    return episodeUUID === "create" ? null : episodeUUID;
  };

  getEpisodeInfo = () => {
    var showUUID = this.getShowUUID();
    return get(this.props.episodesByShow, showUUID);
  };

  getEpisode = () => {
    var episodeInfo = this.getEpisodeInfo();
    var episodeUUID = this.getEpisodeUUID();
    return get(episodeInfo, ["episodes", episodeUUID]);
  };

  getShow = () => {
    var showUUID = this.getShowUUID();
    return get(this.props.shows.shows, showUUID);
  };

  onSubmit = episode => {
    var newEpisode = { ...episode };

    // adjust episode time for epoch
    if (newEpisode.publishedAt) {
      newEpisode.publishedAt = Math.round(newEpisode.publishedAt.valueOf() / 1000);
    }

    //opposite of "save as draft"
    newEpisode.isVisible = !newEpisode.isVisible;

    // Make sure these are treated as numbers.
    if (newEpisode.season) {
      newEpisode.season = parseInt(newEpisode.season, 10);
    }
    if (newEpisode.episodeNumber) {
      newEpisode.episodeNumber = parseInt(newEpisode.episodeNumber, 10);
    }

    // Make sure there is an audio file uploaded on create.
    if (!newEpisode.audioFileData && !this.isEditMode()) {
      this.props.showMessageWithTimeout("An audio file is required.", messageTypes.Error);
    }

    var validator = this.isEditMode()
      ? this.props.validateEpisodeUpdate
      : this.props.validateEpisodeCreate;
    return validator(newEpisode, this.props.user).then(result => {
      if (result.status !== 200) {
        return Promise.reject();
      }
      const uploadMediaFiles = [
        this.uploadImagePromise(newEpisode),
        this.uploadAudioPromise(newEpisode),
      ];
      if (this.shouldInsertMarkers() && newEpisode.audioFileData) {
        this.props.openLoadingModal({
          uploadAndEditEpisode: () => this.uploadAndEditEpisode(uploadMediaFiles, newEpisode, true),
        });
        return;
      }
      return this.uploadAndEditEpisode(uploadMediaFiles, newEpisode, false)
        .then(this.postEdit)
        .catch(e => {
          this.props.setError(e.message);
        });
    });
  };

  uploadAndEditEpisode = (uploadMediaFiles, newEpisode, silent) => {
    return Promise.all(uploadMediaFiles).then(results => {
      const [imageUUID, audioUUID] = results;

      delete newEpisode.imageFileData;
      newEpisode.imageMediaFileUUID = imageUUID;

      if (audioUUID) {
        delete newEpisode.audioFileData;
        newEpisode.contentMediaFileUUID = audioUUID;
      }

      // Bail and rely on the audio upload error to display an error.
      if (!this.isEditMode() && !audioUUID) {
        return;
      }

      return this.submitHandler(newEpisode, this.props.user, silent);
    });
  };
  shouldInsertMarkers = () => {
    const show = this.getShow();
    return (
      show?.canShareAudienceSize ||
      show?.advertisementSettings?.isHostReadEnabled ||
      show?.advertisementSettings?.isPreRecordedEnabled
    );
  };
  postEdit = submitResult => {
    if (submitResult.status === 200) {
      if (this.shouldInsertMarkers()) {
        this.props.history.push(
          `/shows/${submitResult.json.showUUID}/ep/${submitResult.json.uuid}/insertion-points?postUpload=true`
        );
      } else {
        this.close();
      }
    }
  };

  uploadImagePromise = episodeData => {
    if (this.props.imageUploader.touched) {
      if (!this.props.imageUploader.image) return Promise.resolve(null);
      return getCroppedImg(this.props.imageUploader).then(image => {
        var fileName = get(this.props.imageUploader, "fileName");
        var existingURL = get(this.props, [
          "mediaFiles",
          get(this.getEpisode(), "imageMediaFileUUID"),
          "url",
        ]);
        if (existingURL) {
          fileName = existingURL.split("/").pop();
        }

        return this.props.fileUpload(image, fileName, this.props.user).then(result => {
          if (result.status === 200) {
            const imageUUID = get(result, "json[0].uuid");
            return imageUUID;
          }
        });
      });
    }

    return Promise.resolve(episodeData.imageMediaFileUUID);
  };

  uploadAudioPromise = episodeData => {
    const fileTooLargeErorrMessage =
      "Your audio file is too large. RedCircle's max audio file size is 200 MB. Try re-exporting your audio at a lower bitrate.";
    if (episodeData.audioFileData) {
      if (episodeData.audioFileData[0].size > 2 * 10 ** 8) {
        return Promise.reject(new Error(fileTooLargeErorrMessage));
      }
      return this.props
        .fileUpload(
          episodeData.audioFileData[0],
          episodeData.audioFileData[0].name,
          this.props.user,
          e => this.props.uploadProgress(e.loaded / e.total)
        )
        .then(result => {
          if (get(result, "status") === 413) {
            return Promise.reject(new Error(fileTooLargeErorrMessage));
          }
          if (get(result, "json.length")) {
            return get(
              result.json.find(({ bitrate }) => bitrate === 128),
              "uuid",
              get(result, "json[0].uuid")
            );
          }
        });
    }
    return Promise.resolve(episodeData.contentMediaFileUUID);
  };

  submitHandler = (data, user, silent) => {
    return this.isEditMode()
      ? this.props.updateEpisode(data, user, silent)
      : this.props.createEpisode(data, user, silent);
  };

  isEditMode = () => {
    return this.props.mode === "edit";
  };

  getInitialvalues = () => {
    let show = this.getShow();
    let subscriptionsEnabled = !!get(show, ["subscriptionDetails", "amount"]);

    if (this.isEditMode()) {
      var initialValues = { ...this.getEpisode() };
      initialValues.publishedAt *= 1000;
      initialValues.isVisible = !get(this.getEpisode(), "isVisible");
      return initialValues;
    }
    return {
      showUUID: this.getShowUUID(),
      isClosedCaptioned: false,
      shouldPublishToNonSubscribers: true,
      shouldPublishToSubscribers: subscriptionsEnabled,
      isExplicit: false,
      isVisible: false,
      episodeType: "full",
      adPlacements: [0, 0],
      publishedAt: new Date(),
    };
  };

  render() {
    var Form = this.isEditMode() ? EpisodeEditForm : EpisodeCreateForm;
    return (
      <BaseModal
        // Modal props
        isLoading={this.isEditMode() && !this.getEpisode()}
        showContent={Boolean(!this.isEditMode() || this.getEpisode())}
        title={this.isEditMode() ? "Edit Episode" : "Create Episode"}
        close={this.close}
        Content={Form}
        // Passed to Form
        initialValues={this.getInitialvalues()}
        isFormLoading={this.isLoading()}
        progress={this.props.progress}
        setError={this.props.setError}
        mediaFiles={this.props.mediaFiles}
        onSubmit={this.onSubmit}
        show={this.getShow()}
        validationErrors={get(this.getEpisodeInfo(), "validationErrors")}
        fileUpload={this.props.fileUpload}
        setImageFileUUID={uuid => this.props.setImageFileUUID(uuid, this.props.mode)}
        setContentFileUUID={uuid => this.props.setContentFileUUID(uuid, this.props.mode)}
        setImageFileData={data => {
          this.props.setImageFileData(this.props.mode, data);
        }}
        setAudioFileData={data => {
          this.props.setAudioFileData(this.props.mode, data);
        }}
        user={this.props.user}
      />
    );
  }
}

export default EpisodeFormModal;
